import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Building2, FolderKanban, Activity, Mail, Phone } from "lucide-react";
import api from "@/lib/api";
import { PriorityBadge, StatusBadge, Card, CardHeader } from "@/components/common";
import { relativeTime } from "@/lib/format";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: c, isLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => (await api.get(`/clients/${id}`)).data,
  });
  if (isLoading || !c) return <div className="text-sm text-ink-muted">Loading client…</div>;

  return (
    <div data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-4"><ArrowLeft className="w-4 h-4" /> Clients</button>
      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 bg-ink text-white flex items-center justify-center font-heading text-2xl font-bold">{c.name[0]}</div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">{c.name}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-ink-muted">
            <StatusBadge value={c.status} />
            {c.contact_email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {c.contact_email}</span>}
            {c.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.contact_phone}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader title="Jobs" icon={FolderKanban} action={<span className="text-[11px] font-mono text-ink-faint">{c.jobs.length}</span>} />
            <div className="divide-y divide-border">
              {c.jobs.map((j) => (
                <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted transition-colors">
                  <PriorityBadge value={j.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{j.name}</div>
                    <div className="text-xs text-ink-muted">{j.task_label} · {j.deliverable}</div>
                  </div>
                  <StatusBadge value={j.status} />
                </button>
              ))}
              {c.jobs.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">No jobs yet.</div>}
            </div>
          </Card>
        </div>
        <Card>
          <CardHeader title="Timeline" icon={Activity} />
          <div className="divide-y divide-border max-h-[460px] overflow-y-auto">
            {c.timeline.map((t) => (
              <div key={t.id} className="px-4 py-2.5">
                <div className="text-sm text-ink">{t.summary}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] font-semibold text-ink-muted">{t.actor}</span>
                  <span className="font-mono text-[10px] text-ink-faint">{relativeTime(t.created_at)}</span>
                </div>
              </div>
            ))}
            {c.timeline.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">No activity recorded.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
